/* Admin form primitives — extracted from AdminContentEdit to keep that file
 * focused on orchestration and to reduce cyclomatic complexity. */
import React from "react";
import { Trash2, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AdminImageField } from "./AdminImageField";

const INPUT_CLASS = "bg-[rgba(20,22,27,0.6)] border-[rgba(199,194,184,0.18)]";

const makeKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `k_${Math.random().toString(36).slice(2)}_${Date.now()}`;

/* Field renderers — one per `type`. Keeps FieldInput tiny and removes
 * the deeply-nested switch that made it complex. */
const RENDERERS = {
  longtext: ({ value, onChange }) => (
    <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={8} className={INPUT_CLASS} />
  ),
  textarea: ({ value, onChange }) => (
    <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className={INPUT_CLASS} />
  ),
  number: ({ value, onChange }) => (
    <Input
      type="number"
      value={value ?? 0}
      onChange={(e) => onChange(parseInt(e.target.value || "0", 10) || 0)}
      className={INPUT_CLASS}
    />
  ),
  // Nullable decimal field used for constellation coordinates (0-100%).
  // Empty input maps to null so the public page can fall back to the
  // default layout map; any typed value (including 0) is saved as a float.
  position: ({ field, value, onChange }) => (
    <Input
      type="number"
      step="0.1"
      min="0"
      max="100"
      value={value === null || value === undefined ? "" : value}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "") return onChange(null);
        const n = parseFloat(v);
        onChange(Number.isFinite(n) ? n : null);
      }}
      placeholder={field?.hint?.includes("0") ? "0 – 100" : ""}
      className={INPUT_CLASS}
    />
  ),
  select: ({ field, value, onChange }) => (
    <Select value={value || field.options?.[0]?.value} onValueChange={onChange}>
      <SelectTrigger className={INPUT_CLASS}><SelectValue /></SelectTrigger>
      <SelectContent>
        {field.options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  ),
  tags: ({ field, value, onChange }) => {
    const csv = Array.isArray(value) ? value.join(", ") : "";
    return (
      <Input
        value={csv}
        onChange={(e) =>
          onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))
        }
        placeholder={field.hint || "comma-separated"}
        className={INPUT_CLASS}
      />
    );
  },
  image: ({ field, value, onChange }) => (
    <AdminImageField value={value} onChange={onChange} hint={field.hint} />
  ),
  // Ordered list of image pages (used for lyric booklets). Each item is a
  // data URI produced by the same compression pipeline as `image`. UI
  // supports reorder (up/down), remove, and preview.
  image_list: ({ field, value, onChange }) => (
    <ImageListField field={field} value={value} onChange={onChange} />
  ),
  text: ({ value, onChange }) => (
    <Input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className={INPUT_CLASS} />
  ),
};

export function FieldInput({ field, value, onChange }) {
  const Renderer = RENDERERS[field.type] || RENDERERS.text;
  return <Renderer field={field} value={value} onChange={onChange} />;
}

// ---------------------------------------------------------------------
//  ImageListField — reorderable list of image pages for lyric booklets.
// ---------------------------------------------------------------------
function ImageListField({ field, value, onChange }) {
  const pages = Array.isArray(value) ? value : [];
  const setPages = (next) => onChange(next);

  const addPage = (dataUri) => {
    if (!dataUri) return;
    setPages([...pages, dataUri]);
  };
  const removePage = (idx) => setPages(pages.filter((_, i) => i !== idx));
  const movePage = (idx, dir) => {
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= pages.length) return;
    const next = pages.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    setPages(next);
  };

  return (
    <div className="space-y-4">
      {field.hint && (
        <p className="font-mono text-[10px] text-[rgba(199,194,184,0.55)]">{field.hint}</p>
      )}
      {pages.length === 0 && (
        <p className="rounded-lg border border-dashed border-[rgba(199,194,184,0.18)] px-4 py-6 text-center font-serif italic text-sm text-[rgba(231,224,214,0.55)]">
          No pages yet. Add booklet pages below in reading order.
        </p>
      )}
      {pages.map((uri, idx) => (
        <ImageListRow
          key={idx}
          index={idx}
          uri={uri}
          totalPages={pages.length}
          onMoveUp={() => movePage(idx, "up")}
          onMoveDown={() => movePage(idx, "down")}
          onRemove={() => removePage(idx)}
        />
      ))}
      <div className="pt-2 border-t border-[rgba(199,194,184,0.1)]">
        <p className="font-mono tracking-archival text-[10px] text-[rgba(199,194,184,0.55)] mb-2">
          Add page {pages.length + 1}
        </p>
        <AdminImageField
          key={pages.length /* remounts input after each successful add */}
          value=""
          onChange={addPage}
          hint="Uploads compress to ~1600px wide, base64-encoded, stored inline."
        />
      </div>
    </div>
  );
}

function ImageListRow({ index, uri, totalPages, onMoveUp, onMoveDown, onRemove }) {
  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-[rgba(199,194,184,0.12)] bg-[rgba(20,22,27,0.4)] p-3"
      data-testid={`booklet-page-row-${index}`}
    >
      <img
        src={uri}
        alt={`Booklet page ${index + 1}`}
        className="h-16 w-16 rounded object-cover border border-[rgba(199,194,184,0.14)]"
        draggable={false}
      />
      <div className="flex-1 font-mono tracking-archival text-[10px] text-[rgba(231,224,214,0.85)]">
        <div>PAGE {String(index + 1).padStart(2, "0")}</div>
        <div className="text-[rgba(199,194,184,0.55)]">of {totalPages}</div>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={index === 0}
          aria-label={`Move page ${index + 1} up`}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[rgba(199,194,184,0.18)] text-[rgba(231,224,214,0.85)] hover:bg-[rgba(231,224,214,0.04)] hover:text-[rgba(199,168,106,0.95)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >↑</button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={index === totalPages - 1}
          aria-label={`Move page ${index + 1} down`}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[rgba(199,194,184,0.18)] text-[rgba(231,224,214,0.85)] hover:bg-[rgba(231,224,214,0.04)] hover:text-[rgba(199,168,106,0.95)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >↓</button>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove page ${index + 1}`}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[rgba(209,75,75,0.4)] text-[rgba(209,75,75,0.9)] hover:bg-[rgba(209,75,75,0.08)] hover:text-[rgba(209,75,75,1)] transition-colors"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

/* Generates a stable per-item key. Used internally by ListField so React
 * doesn't re-mount form rows when the list reorders / items are removed. */
function ensureKeys(items, itemFields) {
  return (items || []).map((item) => (item._key ? item : { ...item, _key: makeKey() }));
}

function blankItem(itemFields) {
  const out = { _key: makeKey() };
  for (const f of itemFields) {
    out[f.name] = f.type === "number" ? 0 : "";
  }
  return out;
}

export function ListField({ field, value = [], onChange }) {
  // _key is a UI-only field used as a stable React key for each row.
  // KEEP _key in state throughout the editing session — stripping it on
  // every onChange causes `ensureKeys` to regenerate keys on re-render,
  // which remounts the row's DOM and steals focus after each keystroke.
  // Payloads are cleaned server-bound at final save time (see
  // `stripListKeysDeep` in AdminContentEdit's buildPayload).
  const items = ensureKeys(value, field.item_fields);

  const addItem = () => onChange([...items, blankItem(field.item_fields)]);
  const update = (idx, key, v) => {
    const next = items.slice();
    next[idx] = { ...next[idx], [key]: v };
    onChange(next);
  };
  const remove = (idx) => {
    const next = items.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div
          key={item._key}
          className="rounded-md border border-[rgba(199,194,184,0.18)] p-3 bg-[rgba(20,22,27,0.45)]"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono tracking-archival text-[10px] text-[rgba(199,194,184,0.55)]">
              #{idx + 1}
            </span>
            <button
              type="button"
              onClick={() => remove(idx)}
              className="text-[rgba(209,75,75,0.85)] hover:text-[rgba(209,75,75,1)]"
              aria-label="Remove"
            >
              <Trash2 size={14} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {field.item_fields.map((f) => (
              <label key={f.name} className="flex flex-col gap-1 text-sm">
                <span className="font-mono tracking-archival text-[10px] text-[rgba(199,194,184,0.55)]">
                  {f.label}
                </span>
                <FieldInput
                  field={f}
                  value={item[f.name]}
                  onChange={(v) => update(idx, f.name, v)}
                />
                {f.hint && (
                  <span className="font-mono text-[9.5px] text-[rgba(199,194,184,0.4)]">{f.hint}</span>
                )}
              </label>
            ))}
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        onClick={addItem}
        className="border-[rgba(199,194,184,0.2)] text-[rgba(231,224,214,0.85)] hover:bg-[rgba(231,224,214,0.04)] font-mono tracking-archival text-[10.5px]"
      >
        <Plus size={14} className="mr-1" /> Add item
      </Button>
    </div>
  );
}
